import { useMemo } from "react";

import { useAtom } from "jotai";

import EmptyState from "src/components/DashboardRenderer/EmptyState";
import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { BillingSection } from "src/components/SettingsDialog/StoreSettings/BillingSection";
import { DangerZoneSection } from "src/components/SettingsDialog/StoreSettings/DangerZoneSection";
import { DataSyncSection } from "src/components/SettingsDialog/StoreSettings/DataSyncSection";
import { DescriptionSection } from "src/components/SettingsDialog/StoreSettings/DescriptionSection";
import { InputsSection } from "src/components/SettingsDialog/StoreSettings/InputsSection";
import { IntegrationsSection } from "src/components/SettingsDialog/StoreSettings/IntegrationsSection";
import TeamSection from "src/components/SettingsDialog/StoreSettings/TeamSection";
import { StoreSwitcher } from "src/components/Sidebar/StoreSwitcher";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Separator } from "src/components/ui/separator";
import { Skeleton } from "src/components/ui/skeleton";
import { useAuth } from "src/lib/auth";
import { storeIntegrationTypes } from "src/lib/constants";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { isDevRole } from "src/lib/roles";
import { cn } from "src/lib/utils";

const StoreSettingsLoadingState = () => (
  <div className="space-y-6">
    <Skeleton className="h-12 w-full max-w-80" />

    <div className="space-y-6">
      <Skeleton className="h-32 w-full rounded-xl" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
      <Skeleton className="h-48 w-full rounded-xl" />
    </div>
  </div>
);

const StoreSettings = ({
  manageParam,
  sectionTarget,
  highlightedSection,
  clearManageParam,
  setPreventClickOutside,
}) => {
  const type = storeIntegrationTypes.shopify;
  const [, setSettingsDialogState] = useAtom(settingsDialogAtom);
  const {
    selectedIntegration: store,
    updateIntegration,
    deleteIntegration,
    leaveIntegration,
    refetch,
    loading,
    hasLoadedOnce,
  } = useIntegration(type, {
    doPolling: true,
    onLeaveSuccess: () => {
      setSettingsDialogState((prev) => ({
        ...prev,
        open: false,
        manageParam: null,
        sectionTarget: null,
        sectionTargetNonce: 0,
      }));
    },
  });

  const { currentUser } = useAuth();
  const currentMembership = useMemo(() => {
    return store?.users.find((user) => user.user.id === currentUser.id);
  }, [currentUser, store]);
  const isAdmin = useMemo(() => {
    return currentMembership?.role === "ADMIN";
  }, [currentMembership]);
  const isDev = isDevRole(currentUser?.role);
  const isDemoReadOnly = Boolean(store?.isDemo && !isAdmin);
  const canLeave = Boolean(
    currentMembership && !currentMembership.isBillingUser
  );

  if (!hasLoadedOnce && loading) {
    return <StoreSettingsLoadingState />;
  }

  if (!store) {
    return <EmptyState />;
  }

  const highlightSection = (target) =>
    cn(
      "rounded-xl transition-[background-color,box-shadow] duration-700",
      sectionTarget === target &&
        highlightedSection === target &&
        "bg-primary/5 ring-primary/30 ring-2"
    );
  const failingChildIntegrations =
    store.children?.filter((integration) => integration.activeIssue) || [];

  return (
    <div className="space-y-6">
      <StoreSwitcher
        dropdownPosition="bottom-left"
        className="bg-muted/40"
        noSettings
      />

      <div className="space-y-6">
        {!store.isConnected && store.connectionStatusReason && (
          <div className="border-destructive/30 bg-destructive/5 rounded-lg border px-4 py-3 text-sm">
            <div className="text-destructive font-medium">Store paused</div>
            <div className="text-muted-foreground mt-1">
              {store.connectionStatusReason}
            </div>
          </div>
        )}

        {isDemoReadOnly && (
          <div className="border-primary/20 bg-primary/5 rounded-lg border px-4 py-3 text-sm">
            <div className="flex items-center gap-2 font-medium">
              Demo store
              <Badge variant="outline" className="text-primary">
                Read-only
              </Badge>
            </div>
            <div className="text-muted-foreground mt-1">
              This demo workspace uses generated data. Settings, integrations,
              manual inputs, billing, and team details are locked.
            </div>
          </div>
        )}

        {!!failingChildIntegrations.length && (
          <div className="border-destructive/30 bg-destructive/5 rounded-lg border px-4 py-3 text-sm">
            <div className="text-destructive font-medium">
              Marketing integrations need attention
            </div>
            <div className="text-muted-foreground mt-1">
              {failingChildIntegrations
                .map(
                  (integration) => integration.description || integration.type
                )
                .join(", ")}{" "}
              {failingChildIntegrations.length === 1 ? "is" : "are"} currently
              failing to pull data.
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                document
                  .querySelector('[data-settings-section="integrations"]')
                  ?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
              }}
            >
              Jump to integrations
            </Button>
          </div>
        )}

        <DescriptionSection
          store={store}
          updateIntegration={updateIntegration}
          loading={loading}
          isAdmin={isAdmin}
          sectionTarget={sectionTarget}
          highlightedSection={highlightedSection}
        />

        <InputsSection
          store={store}
          updateIntegration={updateIntegration}
          refetchStore={refetch}
          sectionId="manual_inputs"
          className={highlightSection("manual_inputs")}
          readOnly={isDemoReadOnly || !isAdmin}
        />

        {!isDemoReadOnly && (
          <div
            data-settings-section="team"
            className={highlightSection("team")}
          >
            <TeamSection
              store={store}
              setPreventClickOutside={setPreventClickOutside}
              isAdmin={isAdmin}
            />
          </div>
        )}

        {isAdmin && (
          <>
            <BillingSection store={store} />
          </>
        )}

        <IntegrationsSection
          store={store}
          loading={loading}
          isAdmin={isAdmin}
          manageParam={manageParam}
          sectionTarget={sectionTarget}
          highlightedSection={highlightedSection}
          clearManageParam={clearManageParam}
        />

        {(currentMembership || canLeave || isDev) && (
          <>
            <Separator />
            <DangerZoneSection
              store={store}
              deleteIntegration={deleteIntegration}
              leaveIntegration={leaveIntegration}
              loading={loading}
              canDelete={isAdmin && !store.isDemo}
              canLeave={canLeave}
              dataSyncSection={
                isDev && !store.isDemo ? (
                  <DataSyncSection store={store} type={type} />
                ) : null
              }
              showLeave={Boolean(currentMembership)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default StoreSettings;
